import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, AuthService, GenesisUnitOfWork, UtilityService } from 'app/core/core.module';
import { PurchaseOrderInvoice, PurchaseOrderInvoiceLine, PurchaseOrderLineItem, Vendor, VendorTerm } from 'app/core/model/entity-model';
import { Utility } from 'app/core/utility';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-purchase-order-invoice-dialog',
    templateUrl: './purchase-order-invoice-dialog.component.html',
    styleUrls: ['./purchase-order-invoice-dialog.component.scss'],
    standalone: false,
})
export class PurchaseOrderInvoiceDialogComponent implements OnInit {
    result: Subject<any> = new Subject<any>();

    invoice: PurchaseOrderInvoice;
    totalPartsCost: number;
    lineIds: number[];
    lines: PurchaseOrderInvoiceLine[];
    vendor: Vendor;
    vendorTermsDaysOffset = null;
    purchaseOrderLineItems: PurchaseOrderLineItem[];
    isNew = false;
    today: Date;
    modes = ['Pay Full Amount', 'Apply Parts Total'];
    selectedMode: 'Pay Full Amount' | 'Apply Parts Total' = 'Pay Full Amount';

    validationGroup = 'purchaseOrderInvoiceValidationGroup';
    errorAction = 'saving';

    get lineTotals() {
        if (!this.lines) return 0;
        return _(this.lines).sumBy((line) => line.cost);
    }

    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    modalRef = inject(BsModalRef);
    uow = inject(GenesisUnitOfWork);
    modalService = inject(BsModalService);
    utility = inject(UtilityService);

    constructor(
        private appInfo: AppInformationService,
        private auth: AuthService,
    ) {}

    async ngOnInit() {
        this.purchaseOrderLineItems = (await this.uow.purchaseOrderLineItems.whereActive()) as any[];
        this.today = new Date();

        if (!this.invoice) {
            this.isNew = true;
            this.selectedMode = 'Pay Full Amount';
            this.invoice = this.uow.purchaseOrderInvoices.createEntity({
                vendorId: this.vendor.id,
                invoiceDate: moment(this.today).startOf('day').toDate(),
                created: this.today,
                createdBy: this.auth.currentUser.userName,
            }) as PurchaseOrderInvoice;
        }
        await this.getData();
        if (this.isNew) {
            this.invoice.dueDate = !!this.vendorTermsDaysOffset ? moment(this.today).add(this.vendorTermsDaysOffset, 'days').startOf('day').toDate() : moment(this.today).add(30, 'days').startOf('day').toDate();
        }
        console.log(this.invoice);
    }

    async getData() {
        if (this.isNew) {
            this.lines = [];
            const receivingLines = (await this.uow.purchaseOrderLineReceivings.byIds(this.lineIds, 'purchaseOrderLine.part.partAliases')) as any[];
            let total = 0;
            receivingLines.forEach((receiving) => {
                const line = this.uow.purchaseOrderInvoiceLines.createEntity({
                    purchaseOrderInvoiceId: this.invoice.id,
                    purchaseOrderLineReceivingId: receiving.id,
                    purchaseOrderLineItemId: receiving.purchaseOrderLine.purchaseOrderLineItemId,
                    quantity: receiving.quantity,
                    cost: receiving.purchaseOrderLine.cost,
                    created: this.today,
                    createdBy: this.auth.currentUser.userName,
                }) as PurchaseOrderInvoiceLine;
                (line as any).description = receiving.purchaseOrderLine.description;
                this.lines.push(line);
                total += receiving.purchaseOrderLine.cost * receiving.quantity;
            });
            this.invoice.total = total;
            console.log(this.invoice.total);
        } else {
            this.lines = await this.uow.getPurchaseOrderInvoiceLines(this.invoice.id);
            this.vendor = this.invoice.vendor;
        }
        this.vendorTermsDaysOffset = !!this.vendor?.termsId ? (await this.uow.vendorTerms.byId(this.vendor?.termsId) as VendorTerm).daysOffset : null;
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'before',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            }
        );
    }

    onInvoiceDateChanged(e) {
        if (!e.value) return;
        this.invoice.dueDate = !!this.vendorTermsDaysOffset ? moment(e.value).add(this.vendorTermsDaysOffset, 'days').startOf('day').toDate() : moment(e.value).add(30, 'days').startOf('day').toDate();
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            e.cells.forEach((cell) => {
                if (cell.column.dataField === 'cost') {
                    if (cell.cellElement) {
                        cell.cellElement.className = cell.cellElement.className + ' po-amount-cell';
                        if (cell.value === 0) {
                            cell.cellElement.className = cell.cellElement.className + ' zero-amount';
                        }
                    }
                }
            });
        }
    }

    rowDoubleClick(e) {
        if (!this.isNew || this.selectedMode === 'Apply Parts Total') return;

        const line = e.data as PurchaseOrderInvoiceLine;
        if (!line) return;

        line.cost = line.cost === 0 ? line.purchaseOrderLineReceiving.purchaseOrderLine.cost : 0;
        this.linesGrid.instance.refresh();
    }

    modeChanged = (e) => {
        this.totalPartsCost = 0;
    };

    calcInvoiceTotal() {
        this.invoice.total = this.lines.reduce((acc, line) => acc + line.cost * line.quantity, 0);
    }

    applyAveragePartCost() {
        const totalParts = _(this.lines).sumBy((line) => line.quantity);
        const partPercentage = this.totalPartsCost / totalParts;
        let totalApplied = 0;

        for (let index = 0; index < this.lines.length; index++) {
            const line = this.lines[index];
            const lastOne = index === this.lines.length - 1;

            line.cost = lastOne ? Math.abs((totalApplied - this.totalPartsCost) / line.quantity) : Utility.round(partPercentage, 5);
            totalApplied += Utility.round(line.quantity * Utility.round(partPercentage, 5), 2);
        }
        this.calcInvoiceTotal();
        this.linesGrid.instance.refresh();
    }

    addExpenseLine() {
        const expenseItem = _.find(this.purchaseOrderLineItems, { type: 'Expense' });
        const line = this.uow.purchaseOrderInvoiceLines.createEntity({
            purchaseOrderInvoiceId: this.invoice.id,
            purchaseOrderLineReceivingId: null,
            purchaseOrderLineItemId: expenseItem.id,
            quantity: 1,
            cost: 1,
            created: this.today,
            createdBy: this.auth.currentUser.userName,
        }) as PurchaseOrderInvoiceLine;
        this.lines.push(line);
        this.calcInvoiceTotal();
    }
    editLineItem(line: PurchaseOrderInvoiceLine) {
        if (!line) return;
        const originalQbLineItemId = line.purchaseOrderLineItemId;
        console.log('originalQbLineItemId',originalQbLineItemId);

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Edit Line Item',
                dialogValue: line.purchaseOrderLineItemId,
                dialogValueArray: this.purchaseOrderLineItems,
                selectionRequired: true,
                mode: 'selectWithId',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                if (!originalQbLineItemId || originalQbLineItemId !== result.value) {
                    line.purchaseOrderLineItemId = result.value;
                    await this.utility.updateReceivingQuickBooksItem(line.purchaseOrderLineReceivingId, result.value);
                }
                line.purchaseOrderLineItemId = result.value;
                this.calcInvoiceTotal();
            }
            modalRef.hide();
        });
    }

    removeLine(invoiceLine: PurchaseOrderInvoiceLine) {
        if (!invoiceLine) return;

        this.lines.splice(this.lines.indexOf(invoiceLine), 1);
        invoiceLine.entityAspect.setDeleted();
        this.calcInvoiceTotal();
    }

    async save() {
        const success = this.validate();
        if (!success) return;

        const allHaveLineItem = this.lines.every((line) => line.purchaseOrderLineItemId);
        if (!allHaveLineItem) {
            await alert('All lines must have a line item', this.appInfo.settings.company);
            return;
        }
        
        this.lines.forEach((x) => {
            if (x?.purchaseOrderLineReceiving?.purchaseOrderLine?.partAliasId && x.purchaseOrderLineReceiving.purchaseOrderLine.partAlias.quickBooksLineItemId === null) {
                x.purchaseOrderLineReceiving.purchaseOrderLine.partAlias.quickBooksLineItemId = x.purchaseOrderLineItemId;
            }
        });

        this.appInfo.pleaseWaitShow();
        // this.lines.filter((line) => line.cost).forEach((line) => (line.purchaseOrderInvoiceId = this.invoice.id));
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
        this.result.next({ success: true, value: this.invoice });
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    validateTotal = (e) => {
        // rounding to prevent floating point errors
        const lineTotal = Utility.round(
            _(this.lines).sumBy((l) => Utility.round(l.quantity * l.cost, 2)),
            2
        );
        const invoiceTotal = Utility.round(e.value, 2);
        console.log(lineTotal);
        console.log(invoiceTotal);
        return lineTotal === invoiceTotal;
    };

    close() {
        this.uow.rollback();
        this.result.next({ success: false });
    }
}
